/**
 * ViolationManual - Interactive user manual for the Violations module.
 * Located in System Preferences > Violations > Manual tab.
 */
import React, { useState } from "react";
import {
  Box, Typography, Paper, Grid, Chip, Alert, List, ListItem,
  ListItemIcon, ListItemText, Accordion, AccordionSummary,
  AccordionDetails, Divider,
} from "@mui/material";
import ExpandMoreIcon      from "@mui/icons-material/ExpandMore";
import CheckCircleIcon     from "@mui/icons-material/CheckCircle";
import WarningAmberIcon    from "@mui/icons-material/WarningAmber";
import GavelIcon           from "@mui/icons-material/Gavel";
import PeopleIcon          from "@mui/icons-material/People";
import ReceiptLongIcon     from "@mui/icons-material/ReceiptLong";
import TimerIcon           from "@mui/icons-material/Timer";
import MailIcon            from "@mui/icons-material/Mail";
import SettingsIcon        from "@mui/icons-material/Settings";
import LinkIcon            from "@mui/icons-material/Link";
import HistoryIcon         from "@mui/icons-material/History";
import ArticleIcon         from "@mui/icons-material/Article";
import QrCodeIcon          from "@mui/icons-material/QrCode";
import InfoIcon            from "@mui/icons-material/Info";
import GradeIcon           from "@mui/icons-material/Grade";

const cardSx = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:3, mb:2 };
const bodyTextSx = { color:"#374151", lineHeight:1.8 };
const Badge = ({ label, color="#d97706" }) => (
  <Chip size="small" label={label} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", mr:0.5, mb:0.5, bgcolor:`${color}18`, color }} />
);

// ── Sections ────────────────────────────────────────────────────────

const OverviewSection = () => (
  <Box>
    <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb:2.5, borderRadius:2 }}>
      The Violations module tracks CCR (Covenant, Condition & Restriction) violations from initial notice through resolution, fines, and appeals. Every step is logged for legal compliance.
    </Alert>
    <Grid container spacing={2}>
      {[
        { icon:<WarningAmberIcon sx={{ color:"#d97706" }} />, title:"Create & Track", desc:"Log any CCR violation with type, property, status, and cure period." },
        { icon:<TimerIcon sx={{ color:"#2f63ad" }} />,        title:"Cure Days",      desc:"Track the time given to the homeowner to correct the violation before escalation." },
        { icon:<ReceiptLongIcon sx={{ color:"#7c3aed" }} />,  title:"Fines",          desc:"Manually or automatically generate fines based on status triggers. Recurring fines supported." },
        { icon:<ArticleIcon sx={{ color:"#16a34a" }} />,      title:"Letters",        desc:"Generate and send violation notice letters from templates with member data pre-filled." },
        { icon:<GavelIcon sx={{ color:"#dc2626" }} />,        title:"Appeals",        desc:"Record and track homeowner appeals with status tracking and full history." },
        { icon:<MailIcon sx={{ color:"#0369a1" }} />,         title:"Notifications",  desc:"Automatic email alerts on status change, assignment, comment, and fine creation." },
      ].map(({ icon, title, desc }) => (
        <Grid item xs={12} sm={6} key={title}>
          <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", p:2, height:"100%" }}>
            <Box sx={{ display:"flex", gap:1.5, alignItems:"flex-start" }}>
              <Box sx={{ mt:0.25 }}>{icon}</Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142" }}>{title}</Typography>
                <Typography variant="body2" sx={{ color:"#6b778c", mt:0.25 }}>{desc}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const CreatingSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Violations can be created from the main Violations list or directly from a member's profile page.
    </Typography>
    {[
      { step:"1", title:"Select the property", desc:"Search by address, account number, or owner name. The member's contact info populates automatically." },
      { step:"2", title:"Choose a violation type", desc:"Select from your configured CCR types (e.g. Landscaping, Parking, Noise). The type determines the default cure period and fine rules." },
      { step:"3", title:"Set the cure days", desc:"Enter how many days the homeowner has to correct the violation before escalation. This starts the countdown timer." },
      { step:"4", title:"Add details and evidence", desc:"Enter a description, notes, and upload photos as evidence. Files can also be added later from the Details tab." },
      { step:"5", title:"Assign a violation officer", desc:"Assign the violation to a staff member who will monitor it. They receive an email notification." },
      { step:"6", title:"Submit", desc:"The violation is created, logged, and the homeowner can receive an automatic email if configured." },
    ].map(({ step, title, desc }) => (
      <Box key={step} sx={{ display:"flex", gap:2, mb:1.5 }}>
        <Box sx={{ minWidth:28, height:28, borderRadius:"50%", bgcolor:"rgba(217,119,6,0.12)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Typography variant="caption" sx={{ fontWeight:800, color:"#d97706" }}>{step}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{title}</Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>{desc}</Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const StatusSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Statuses reflect where a violation is in the enforcement process. Common workflow:
    </Typography>
    <Box sx={{ mb:2 }}>
      <Badge label="Open" color="#2f63ad" />
      <Badge label="Notice Sent" color="#0369a1" />
      <Badge label="Pending Cure" color="#b45309" />
      <Badge label="Escalated" color="#dc2626" />
      <Badge label="Fine Issued" color="#7c3aed" />
      <Badge label="Resolved" color="#16a34a" />
      <Badge label="Closed" color="#374151" />
    </Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Changing the status (from the Details tab dropdown) will:
    </Typography>
    <List dense>
      {[
        "Record the change in the Status History tab with who made it and when",
        "Trigger an email notification to the homeowner if configured for that status",
        "Automatically generate a fine if a billing rule is set for that status transition",
        "Restart the cure days timer if configured to do so on that status",
      ].map(t => (
        <ListItem key={t} sx={{ py:0.25 }}>
          <ListItemIcon sx={{ minWidth:28 }}><CheckCircleIcon fontSize="small" sx={{ color:"#16a34a" }} /></ListItemIcon>
          <ListItemText primary={<Typography variant="body2" sx={{ color:"#374151" }}>{t}</Typography>} />
        </ListItem>
      ))}
    </List>
    <Alert severity="info" sx={{ mt:2, borderRadius:2 }}>
      To add, rename, or reorder statuses go to System Preferences &rarr; Violations &rarr; Statuses.
    </Alert>
  </Box>
);

const CureDaysSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Cure days define the timeframe the homeowner has to correct the violation.
      The countdown is shown in the Details tab and in the violation list.
    </Typography>
    <Grid container spacing={2} sx={{ mb:2 }}>
      <Grid item xs={12} sm={4}>
        <Paper elevation={0} sx={{ borderRadius:"12px", border:"2px solid rgba(22,163,74,0.2)", p:2, textAlign:"center" }}>
          <Typography variant="h4" sx={{ fontWeight:800, color:"#16a34a" }}>Green</Typography>
          <Typography variant="caption" sx={{ color:"#6b778c" }}>More than 3 days remaining</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Paper elevation={0} sx={{ borderRadius:"12px", border:"2px solid rgba(217,119,6,0.3)", p:2, textAlign:"center" }}>
          <Typography variant="h4" sx={{ fontWeight:800, color:"#d97706" }}>Yellow</Typography>
          <Typography variant="caption" sx={{ color:"#6b778c" }}>3 days or fewer remaining</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Paper elevation={0} sx={{ borderRadius:"12px", border:"2px solid rgba(220,38,38,0.3)", p:2, textAlign:"center" }}>
          <Typography variant="h4" sx={{ fontWeight:800, color:"#dc2626" }}>Red</Typography>
          <Typography variant="caption" sx={{ color:"#6b778c" }}>Deadline passed - overdue</Typography>
        </Paper>
      </Grid>
    </Grid>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Restarting Cure Days</Typography>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:1 }}>
      If a homeowner needs more time, you can restart the cure period from the Cure Days panel (Details tab).
      Enter the new number of days and an optional reason. An audit record is created automatically.
    </Typography>
    <Alert severity="warning" sx={{ borderRadius:2 }}>
      Restarting cure days creates a new audit record but does not automatically notify the homeowner.
      Use the Notice tab or Send via Email to notify them of the extension.
    </Alert>
  </Box>
);

const FinesSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Fines are financial penalties issued for violations. They can be created manually or triggered automatically by status changes.
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Manual Fine</Typography>
    <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
      Open the violation, go to the Fines tab, and click "Add Fine". Enter the amount, billing code, and optional notes.
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Automatic Fine (Status Trigger)</Typography>
    <Typography variant="body2" sx={{ color:"#6b778c", mb:1 }}>
      In System Preferences &rarr; Violations &rarr; Billing Setup, configure a rule that triggers a fine when the violation moves to a specific status (e.g. automatically fine when status changes to "Escalated").
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Recurring Fines</Typography>
    <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
      Enable recurring fines to automatically add a fine amount on a schedule (daily, weekly, or monthly) until the violation is resolved.
    </Typography>
    <Alert severity="info" sx={{ borderRadius:2 }}>
      All fines are sent to Integrations &rarr; Billing for review before syncing to Jonas or other accounting systems.
    </Alert>
  </Box>
);

const LettersSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Violation letters (notices) can be generated and emailed or printed directly from the violation detail page.
    </Typography>
    {[
      { title:"Notice tab", desc:"The Notice tab in the violation detail shows the full formatted letter with member info, violation type, cure days, and property address pre-filled from the record." },
      { title:"Letter Templates", desc:"Admins can manage letter templates in System Preferences > Violations > Advanced Settings > Letter Templates. Templates support placeholder variables like {member_name}, {property_address}, {cure_days}, {violation_type}." },
      { title:"Sending a letter", desc:"From the Notice tab, click 'Send via Email' to deliver the letter to the homeowner's email on file. A send event is logged in the letter history." },
      { title:"Letter history", desc:"Each violation keeps a log of all letters sent - date, time, recipient, and who sent it." },
      { title:"PDF upload", desc:"You can upload an externally prepared letter PDF directly to the violation for record-keeping." },
    ].map(({ title, desc }) => (
      <Paper key={title} elevation={0} sx={{ borderRadius:"10px", border:"1px solid rgba(47,99,173,0.08)", p:1.5, mb:1 }}>
        <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{title}</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c", mt:0.25 }}>{desc}</Typography>
      </Paper>
    ))}
  </Box>
);

const AppealsSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Homeowners may formally dispute a violation. The Appeals tab records and tracks the appeal process.
    </Typography>
    {[
      { step:"1", title:"Homeowner submits appeal",   desc:"Staff logs the appeal in the Appeals tab with the appeal date and reason provided by the homeowner." },
      { step:"2", title:"Board review",               desc:"The appeal status is updated as the board reviews it. Statuses: Pending, Under Review, Approved, Denied." },
      { step:"3", title:"Decision recorded",          desc:"The outcome is recorded with notes. If approved, the violation can be updated or closed accordingly." },
      { step:"4", title:"Homeowner notification",     desc:"Notify the homeowner of the decision via the Send via Email button or the Notice tab." },
    ].map(({ step, title, desc }) => (
      <Box key={step} sx={{ display:"flex", gap:2, mb:1.5 }}>
        <Box sx={{ minWidth:28, height:28, borderRadius:"50%", bgcolor:"rgba(124,58,237,0.1)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Typography variant="caption" sx={{ fontWeight:800, color:"#7c3aed" }}>{step}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{title}</Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>{desc}</Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const CommentsSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      The Comments tab on each violation acts as the communication thread between staff and can be shared with the homeowner.
    </Typography>
    {[
      { type:"External", color:"#2f63ad", desc:"Visible to the homeowner if they have portal access. Use for update notifications and cure progress." },
      { type:"Internal", color:"#b45309", desc:"Only visible to staff. Use for enforcement notes, board discussions, or legal observations." },
    ].map(({ type, color, desc }) => (
      <Paper key={type} elevation={0} sx={{ borderRadius:"10px", border:`1px solid ${color}30`, p:1.5, mb:1 }}>
        <Chip size="small" label={type} sx={{ bgcolor:`${color}18`, color, fontWeight:700, fontSize:11, borderRadius:"6px", mb:0.5 }} />
        <Typography variant="body2" sx={{ color:"#374151" }}>{desc}</Typography>
      </Paper>
    ))}
    <Typography variant="body2" sx={{ color:"#6b778c", mt:1 }}>
      All comments include the author name, timestamp, and can include file attachments.
      Edits to comments are tracked with a full change history viewable by admins.
    </Typography>
  </Box>
);

const AdminSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      All Violations settings are in System Preferences &rarr; Violations.
    </Typography>
    <Grid container spacing={1.5}>
      {[
        { tab:"Violation Types",  desc:"Create and manage CCR violation types (Landscaping, Parking, Noise, etc.). Each type can have a default cure period and fine amount." },
        { tab:"Billing Setup",    desc:"Configure auto-fine rules triggered by status changes. Set billing codes, amounts, trigger statuses, and recurring schedules." },
        { tab:"Statuses",         desc:"Add, rename, reorder statuses. Existing violation records retain their current status value when statuses are renamed." },
        { tab:"Form Builder",     desc:"Control which fields appear on the Add Violation form and rename labels to match your HOA's terminology." },
        { tab:"Advanced",         desc:"SLA targets per type, escalation rules, letter templates, webhooks, QR code and survey settings." },
        { tab:"Manual",           desc:"This page. Reference guide for staff and administrators." },
      ].map(({ tab, desc }) => (
        <Grid item xs={12} sm={6} key={tab}>
          <Paper elevation={0} sx={{ borderRadius:"10px", border:"1px solid rgba(47,99,173,0.08)", p:1.5 }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:"#d97706" }}>{tab}</Typography>
            <Typography variant="body2" sx={{ color:"#6b778c", mt:0.25, fontSize:12 }}>{desc}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const PermissionsSection = () => (
  <Box>
    <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr style={{ background:"rgba(47,99,173,0.04)" }}>
            {["Feature","Required Permission"].map(h => (
              <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:12, fontWeight:700, color:"#223142", borderBottom:"1px solid rgba(47,99,173,0.08)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[
            ["View Violations",       "view_violations (or authenticated)"],
            ["Create Violation",      "create_violations"],
            ["Edit Violation",        "edit_violations"],
            ["Delete / Soft Delete",  "delete_violations"],
            ["Assign Violation",      "edit_violations"],
            ["Add / Edit Fines",      "manage_billing_settings"],
            ["Manage Letter Templates","Admin or System Admin role"],
            ["System Preferences",    "Admin or System Admin role"],
          ].map(([feat, perm]) => (
            <tr key={feat} style={{ borderBottom:"1px solid rgba(47,99,173,0.05)" }}>
              <td style={{ padding:"7px 12px", fontSize:12, color:"#223142", fontWeight:600 }}>{feat}</td>
              <td style={{ padding:"7px 12px", fontSize:12, color:"#6b778c", fontFamily:"monospace" }}>{perm}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Paper>
  </Box>
);

const FAQSection = () => {
  const items = [
    {
      q:"How do I stop a homeowner from receiving violation emails?",
      a:"Change the violation status to one that has email notifications disabled, or go to System Preferences > Violations > Advanced > and turn off email triggers for specific status transitions. You can also use Internal-only comments to avoid triggering external emails.",
    },
    {
      q:"Can I recover a deleted violation?",
      a:"Yes - violations use soft delete (data is never removed). An administrator can restore them via the API (POST /api/violations/:id/restore) or by contacting your system administrator.",
    },
    {
      q:"Why isn't the fine appearing in the billing export?",
      a:"Fines must be reviewed and approved in Integrations > Billing before they appear in exports. Check that the billing code is configured in System Preferences > Violations > Billing Setup.",
    },
    {
      q:"The cure days timer is counting wrong.",
      a:"The timer starts from the date the violation (or the last cure days restart) was created. If you updated the cure days after creation, the new period starts from the restart date, not the original creation date.",
    },
    {
      q:"How do I bulk close multiple violations?",
      a:"From the Violations list page, select multiple violations using the checkboxes and use the Bulk Status Change option to move them all to Closed or Resolved at once.",
    },
    {
      q:"What is the QR & Survey tab for?",
      a:"The QR tab generates a unique QR code for the violation that can be printed on physical notices. When scanned, it links to the violation status page. The Survey tab links a satisfaction survey to this violation if configured in Advanced Settings.",
    },
  ];
  return (
    <Box>
      {items.map(({ q, a }) => (
        <Accordion key={q} elevation={0}
          sx={{ border:"1px solid rgba(47,99,173,0.08)", borderRadius:"10px !important", mb:1, "&:before":{ display:"none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ color:"#374151" }}>{a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// ── Main ─────────────────────────────────────────────────────────────
const SECTIONS = [
  { label:"Overview",        icon:<WarningAmberIcon fontSize="small" />, component:<OverviewSection /> },
  { label:"Creating",        icon:<GavelIcon fontSize="small" />,        component:<CreatingSection /> },
  { label:"Statuses",        icon:<HistoryIcon fontSize="small" />,      component:<StatusSection /> },
  { label:"Cure Days",       icon:<TimerIcon fontSize="small" />,        component:<CureDaysSection /> },
  { label:"Fines",           icon:<ReceiptLongIcon fontSize="small" />,  component:<FinesSection /> },
  { label:"Letters",         icon:<ArticleIcon fontSize="small" />,      component:<LettersSection /> },
  { label:"Appeals",         icon:<GradeIcon fontSize="small" />,        component:<AppealsSection /> },
  { label:"Comments",        icon:<MailIcon fontSize="small" />,         component:<CommentsSection /> },
  { label:"Admin Settings",  icon:<SettingsIcon fontSize="small" />,     component:<AdminSection /> },
  { label:"Permissions",     icon:<InfoIcon fontSize="small" />,         component:<PermissionsSection /> },
  { label:"FAQ",             icon:<InfoIcon fontSize="small" />,         component:<FAQSection /> },
];

const ViolationManual = () => {
  const [selected, setSelected] = useState(0);
  return (
    <Box>
      <Box sx={{ mb:3 }}>
        <Typography variant="h6" sx={{ fontWeight:800, color:"#223142" }}>Violations - User Manual</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c" }}>
          Reference guide for violation officers, administrators, and board members.
        </Typography>
      </Box>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden", position:"sticky", top:16 }}>
            {SECTIONS.map(({ label, icon }, i) => (
              <Box key={label} onClick={() => setSelected(i)}
                sx={{ display:"flex", alignItems:"center", gap:1.5, px:2, py:1.25, cursor:"pointer",
                  bgcolor: selected===i ? "rgba(217,119,6,0.08)" : "transparent",
                  borderLeft: selected===i ? "3px solid #d97706" : "3px solid transparent",
                  "&:hover":{ bgcolor:"rgba(217,119,6,0.04)" }, transition:"all 0.15s" }}>
                <Box sx={{ color: selected===i ? "#d97706" : "#6b778c" }}>{icon}</Box>
                <Typography variant="body2" sx={{ fontWeight: selected===i ? 700 : 500, color: selected===i ? "#d97706" : "#374151", fontSize:13 }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper elevation={0} sx={{ ...cardSx, mb:0 }}>
            <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:2.5, pb:2, borderBottom:"1px solid rgba(47,99,173,0.08)" }}>
              <Box sx={{ color:"#d97706" }}>{SECTIONS[selected].icon}</Box>
              <Typography variant="h6" sx={{ fontWeight:800, color:"#223142" }}>{SECTIONS[selected].label}</Typography>
            </Box>
            {SECTIONS[selected].component}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViolationManual;
